import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
    getServiceDetails,
    setMapWorkflow, setOpenDrawer, setBouncePin
} from "../../../../../../features/senninha/pinsSlice";
import { useDispatch, useSelector, useStore } from "react-redux";
import HoverContainer from "./HoverContainer";
import { GingaIcon } from "@stonelog/stonelog-ginga-icons";
import {
    makeSelectIsPinSelected,
} from "../../../../../../features/senninha/routesManagementSlice";
import { applyRelocationSelection } from "../Drawers/RoutesDrawer/utils/applyPinSelection";
import { toCapitalizeFirstLetterOfEachWord } from "../../../../../../consumingApi/services/helper";
import { setHunterPinClicked, setOldSenninhaWorkFlow } from "../../../../../../features/senninha/oldSenninhaSlice";
import "./styles.css";

import * as S from "./styles";


const hasValidPosition = (position) =>
    Number.isFinite(position?.lat) && Number.isFinite(position?.lng);

const SERVICE_LABEL_MAP = {
    'DESINSTALAÇÃO': 'Desin.',
    'ENVIO DE CARTAO': 'Env. Cart.',
    'ENVIO DE SUPRIMENTOS': 'Supri.',
    'INSTALAÇÃO': 'Inst.',
    'MANUTENÇÃO': 'Manut.',
    'TROCA': 'Troca',
    'COLETA': 'Coleta',
    'ENTREGA': 'Entrega',
    'CAMPANHA': 'Camp.'
}

const Marker = ({
    idx,
    map,
    position,
    dataPin
}) => {
    const rootRef = useRef();
    const markerRef = useRef();
    const handlerRef = useRef();
    const containerRef = useRef();

    const [hoverMarker, setHoverMarker] = useState(false)

    const closeHover = () => {
        setHoverMarker(false);
        if (markerRef.current) markerRef.current.zIndex = (position.lat * -100000) << 5;
    }

    // Fallback: se o mouseleave do DOM não disparar (movimento rápido, marker
    // sobreposto, etc.), um listener global de mousemove fecha o card quando
    // o cursor não está mais sobre o container do marker.
    useEffect(() => {
        if (!hoverMarker) return;
        const handleGlobalMove = (e) => {
            const el = containerRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const inside =
                e.clientX >= rect.left &&
                e.clientX <= rect.right &&
                e.clientY >= rect.top &&
                e.clientY <= rect.bottom;
            if (!inside) closeHover();
        };
        document.addEventListener('mousemove', handleGlobalMove);
        return () => document.removeEventListener('mousemove', handleGlobalMove);
    }, [hoverMarker]);

    const dispatch = useDispatch()
    const store = useStore()

    // Subscrições granulares: cada uma só dispara re-render quando o valor
    // específico desse pin muda. Evita o re-render em cascata.
    const mapWorkflow = useSelector((state) => state.pinsSlice.mapWorkflow)
    const openDrawer = useSelector((state) => state.pinsSlice.openDrawer)
    const isBounced = useSelector((state) => state.pinsSlice.bouncedPin === dataPin?.id)
    const isCurrentServicePin = useSelector(
        (state) => state.pinsSlice.serviceDetails?.pinIdentifiers?.id === dataPin?.id
    )
    const selectIsPinSelected = useMemo(() => makeSelectIsPinSelected(dataPin), [dataPin?.id, dataPin?.id_atendimento])
    const isSelected = useSelector(selectIsPinSelected)

    // Bounce: dispara o reset do bouncedPin 2s após esse pin entrar no estado "bouncing".
    useEffect(() => {
        if (!isBounced) return;
        const timeout = setTimeout(() => {
            dispatch(setBouncePin(''))
        }, 2000)
        return () => clearTimeout(timeout)
    }, [isBounced, dispatch]);

    const handleMarkerClick = () => {
        const state = store.getState()
        const { mapWorkflow: currentMapWorkflow } = state.pinsSlice
        const { selectedHubInfos } = state.hubsSlice

        if (currentMapWorkflow === 'infoDrawer' && dataPin?.servico !== '0TPV') {
            dispatch(
                getServiceDetails({
                    id: dataPin.id,
                    id_atendimento: dataPin?.id_atendimento,
                    provider: selectedHubInfos.children,
                    servico: dataPin.servico,
                    id_os: dataPin.oss[0].id_os
                })
            )
            dispatch(
                setOpenDrawer(true),
                setMapWorkflow('infoDrawer')
            )
        }

        if (dataPin?.servico === '0TPV') {
            dispatch(setOldSenninhaWorkFlow('pinHunter'))
            dispatch(setHunterPinClicked(dataPin))
        }

        if (currentMapWorkflow === 'routesDrawer-relocation') {
            applyRelocationSelection({ dataPin, state, dispatch })
        }
    };

    // Mantém a ref do handler atualizada sem recriar listener.
    handlerRef.current = handleMarkerClick;

    const CustomMarker = () => {
        const isBeingCheckedInInfoDrawer = mapWorkflow.includes('infoDrawer') && openDrawer

        return (
            <S.MarkerContainer
                ref={containerRef}
                onMouseEnter={() => {
                    setHoverMarker(true);
                    if (markerRef.current) markerRef.current.zIndex = (position.lat * -1000000) << 5;
                }}
                onMouseLeave={closeHover}
                background={isBeingCheckedInInfoDrawer ?
                    (isCurrentServicePin ? "#0057D9" : "#D0D5DD")
                    : dataPin?.stylePin?.color
                }
                isSelected={isSelected}
                className={isBounced ? 'bounce' : ''}
            >
                <S.MarkerLeftContent>
                    <GingaIcon name={dataPin?.stylePin.name} size={'12px'} color="white" />
                </S.MarkerLeftContent>
                <S.MarkerRightContent>
                    {dataPin?.servico ? (SERVICE_LABEL_MAP[dataPin?.servico] ?? toCapitalizeFirstLetterOfEachWord(dataPin?.servico)) : ''}
                </S.MarkerRightContent>
                {hoverMarker && (
                    <HoverContainer dataPin={dataPin} />
                )}
                {
                    dataPin?.servico === '0TPV' && dataPin?.ineficiencia !== null && (
                        <S.MarkerLeftContent>
                            <GingaIcon name={'warning'} size={'12px'} color="white" />
                        </S.MarkerLeftContent>
                    )
                }
            </S.MarkerContainer>
        )
    }

    // Setup uma única vez: cria o AdvancedMarkerElement e registra o listener
    // de click que delega para handlerRef.current (sempre atualizado).
    useEffect(() => {
        if (!map || !hasValidPosition(position)) return;

        const container = document.createElement("div");
        rootRef.current = createRoot(container);

        markerRef.current = new google.maps.marker.AdvancedMarkerElement({
            position,
            content: container,
            zIndex: (position.lat * -100000) << 5
        })

        const listener = markerRef.current.addListener("click", (e) => {
            if (handlerRef.current) handlerRef.current(e);
        });

        return () => {
            listener.remove();
            if (markerRef.current) markerRef.current.map = null;
            if (rootRef.current) {
                const root = rootRef.current;
                rootRef.current = null;
                queueMicrotask(() => root.unmount());
            }
        };
    }, []);

    // Re-render do conteúdo + reposicionamento. Roda quando qualquer valor
    // que afeta este pin muda. Sem listener thrash (listener vive no effect acima).
    useEffect(() => {
        if (!rootRef.current || !markerRef.current || !map || !hasValidPosition(position)) return;
        rootRef.current.render(<CustomMarker />);
        markerRef.current.position = position;
        markerRef.current.map = map;
    });

    return null;
}

// Comparador custom: o pai cria `position={{ lat, lng }}` inline a cada render,
// então comparamos por valor para não invalidar a memoização à toa.
const areEqual = (prev, next) => (
    prev.map === next.map
    && prev.dataPin === next.dataPin
    && prev.position?.lat === next.position?.lat
    && prev.position?.lng === next.position?.lng
)

export default memo(Marker, areEqual)

